import React, { useState } from 'react';
import { useGetTransactionsByMerchantQuery } from 'store/services/merchantApi';
import { useMerchantDetailContext } from 'contexts/MerchantDetailContext';
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AppTable from 'components/Shared/AppTable';
import BreadcrumbLayout from 'layouts/BreadcrumbLayout';
import HighlightedText from 'components/Shared/HighlightedText';
import { useTablePagination } from 'hooks';

const TransactionTab = () => {
  const { merchantDetail } = useMerchantDetailContext();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');

  const { data: transactionsData, isLoading, isFetching } = useGetTransactionsByMerchantQuery(
    {
      merchantId: merchantDetail?._id,
      sortBy,
      sortOrder,
      search,
      page: currentPage,
      limit: perPage,
    },
    { skip: !merchantDetail?._id }
  );

  const transactions = transactionsData?.data || [];
  const totalItems = transactionsData?.totalItems || 0;

  const handleSort = (column, sortDirection) => {
    setSortBy(column.id);
    setSortOrder(sortDirection);
  };

  const getIdLink = (transaction) => {
    switch (transaction.type) {
      case 'Fuel':
        return `/app/crm/vehicle-fuel/${transaction.objectId}/detail`;
      case 'Maintenance':
        return `/app/crm/vehicle-maintenance/${transaction.objectId}/detail`;
      case 'Insurance':
        return `/app/crm/vehicle/${transaction.objectId}/detail?tab=insurance`;
      default:
        return '#';
    }
  };

  const columns = [
    {
      name: 'Type',
      selector: (row) => row.type,
      sortable: true,
      id: 'type',
    },
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      id: 'id',
      cell: (row) => (
        <Link
          title={row.id}
          to={getIdLink(row)}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row.id ? (
            <HighlightedText text={row.id} highlight={search} />
          ) : (
            'N/A'
          )}
        </Link>
      ),
    },
    {
      name: 'Amount (Rs.)',
      selector: (row) => row.amount,
      sortable: true,
      id: 'amount',
      cell: (row) => row.amount.toFixed(2),
    },
    {
      name: 'Details',
      selector: (row) => row.details,
      cell: (row) =>
        row.type === 'Insurance' ? (
          <Link
            title={row.details}
            to={`/app/crm/vehicle/${row.objectId}/detail?tab=insurance`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row.details ? (
              <HighlightedText text={row.details} highlight={search} />
            ) : (
              'N/A'
            )}
          </Link>
        ) : (
          row.details || 'N/A'
        ),
    },
    {
      name: 'Date',
      selector: (row) => row.createdAt,
      sortable: true,
      id: 'createdAt',
      cell: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <BreadcrumbLayout.Paper>
   <Box display="flex" justifyContent="flex-end" mb={1} mt={1}>
  <TextField
    label="Search Transactions"
    variant="outlined"
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    size="small"
    sx={{
      width: '250px',
      mr: 2, // <-- pushes it slightly to the left
      '& .MuiInputBase-input': {
        paddingY: '10px',
      },
    }}
  />
</Box>

      {transactionsData ? (
        <AppTable
          columns={columns}
          data={transactions}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={totalItems}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          sortServer
          onSort={handleSort}
        />
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" marginLeft={1}>
            Loading Transactions...
          </Typography>
        </Box>
      )}
    </BreadcrumbLayout.Paper>
  );
};

export default TransactionTab;